import React, { useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ColorChips, ConditionChips, BrandChips } from "@/components/AttributeChips";
import { useLang } from "@/context/LanguageContext";
import { PhoneInput } from "@/components/PhoneInput";
import { api } from "@/lib/api";

const field = "mt-1.5 h-11 rounded-xl border-findo-navy/20 focus-visible:ring-findo-red/40 focus-visible:border-findo-red";
const Req = () => <span className="text-findo-red">*</span>;

// Prefill blanks from the logged-in profile (user may load after mount); never overwrite typed values.
export function usePrefillFromProfile(user, setForm) {
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      owner_first_name: f.owner_first_name || user.first_name || "",
      owner_last_name: f.owner_last_name || user.last_name || "",
      owner_email: f.owner_email || user.email || "",
      owner_phone: f.owner_phone || user.phone || "",
      city: f.city || user.city || "",
      country: f._countryTouched ? f.country : (user.country || f.country),
    }));
  }, [user, setForm]);
}

export const EXTRA_DEFAULTS = {
  owner_first_name: "", owner_last_name: "", city: "", circumstances: "", color: "", condition: "", brand: "", quantity: 1,
};

export function extraPayload(form) {
  return {
    owner_first_name: form.owner_first_name.trim(), owner_last_name: form.owner_last_name.trim(),
    owner_name: `${form.owner_first_name.trim()} ${form.owner_last_name.trim()}`.trim(),
    city: form.city.trim(), circumstances: form.circumstances.trim(), color: form.color.trim(),
    condition: form.condition.trim(), brand: form.brand.trim() || null, quantity: Number(form.quantity) || 1,
  };
}

// Declarant identity always comes from the signed-in account (form values only as fallback).
export function accountIdentity(user, form) {
  const [fn, ...rest] = (user?.name || "").trim().split(/\s+/);
  return {
    owner_first_name: user?.first_name || fn || form.owner_first_name || "",
    owner_last_name: user?.last_name || rest.join(" ") || form.owner_last_name || "",
    owner_email: user?.email || form.owner_email || "",
    owner_phone: user?.phone || form.owner_phone || "",
  };
}

// Old accounts without a phone number: ask for it once, at the last step.
export const MissingPhoneField = ({ user, form, setForm, prefix }) => {
  const { t } = useLang();
  if (!user || user.phone) return null;
  return (
    <div data-testid={`${prefix}-missing-phone-block`}>
      <Label>{t("mc.phone")} <Req /></Label>
      <PhoneInput country={form.country} value={form.owner_phone} onChange={(v) => setForm((f) => ({ ...f, owner_phone: v }))} required testid={`${prefix}-missing-phone`} />
      <p className="mt-1 text-xs text-slate-400">{t("acc.phoneSaved")}</p>
    </div>
  );
};

// True (and focuses the field) when an account without phone hasn't filled the missing-phone field.
export function phoneMissing(user, form, prefix) {
  if (!user || user.phone || (form.owner_phone || "").trim().length >= 4) return false;
  document.querySelector(`[data-testid="${prefix}-missing-phone"]`)?.focus();
  return true;
}

// "Published as …" reminder of the account identity that will be shared.
export const PublishedAs = ({ user, testid }) => {
  const { t } = useLang();
  if (!user) return null;
  return (
    <div className="rounded-xl bg-slate-50 border border-border px-3 py-2.5 text-xs text-slate-500" data-testid={testid}>
      {t("anf.publishedAs")} <span className="font-semibold text-findo-navy">{user.name}</span> · {user.email}{user.phone ? ` · ${user.phone}` : ""}
    </div>
  );
};

export async function savePhoneIfMissing(user, phone, refreshUser) {
  if (!user || user.phone || !phone) return;
  try { await api("/b2c/profile", { method: "PATCH", b2c: true, body: { phone } }); await refreshUser?.(); } catch { /* best effort */ }
}

export const IdentityFields = ({ form, setForm, prefix }) => {
  const { t } = useLang();
  return (
    <div className="grid grid-cols-2 gap-3">
      <div><Label>{t("hc.firstName")} <Req /></Label><Input required autoComplete="given-name" value={form.owner_first_name} onChange={(e) => setForm({ ...form, owner_first_name: e.target.value })} className={field} data-testid={`${prefix}-first-name`} /></div>
      <div><Label>{t("hc.lastName")} <Req /></Label><Input required autoComplete="family-name" value={form.owner_last_name} onChange={(e) => setForm({ ...form, owner_last_name: e.target.value })} className={field} data-testid={`${prefix}-last-name`} /></div>
    </div>
  );
};

export const ItemDetailFields = ({ form, setForm, prefix, found, category }) => {
  const { t } = useLang();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-3.5" data-testid={`${prefix}-extra-fields`}>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>{t("hc.city")} <Req /></Label><Input required value={form.city} onChange={set("city")} className={field} data-testid={`${prefix}-city`} /></div>
        <div><Label>{t("hc.quantity")} <Req /></Label><Input required type="number" min={1} max={99} value={form.quantity} onChange={set("quantity")} className={field} data-testid={`${prefix}-quantity`} /></div>
      </div>
      <div><Label>{found ? t("hc.circumstancesFound") : t("hc.circumstancesLost")} <Req /></Label>
        <Textarea required minLength={3} rows={2} value={form.circumstances} onChange={set("circumstances")} placeholder={found ? t("hc.circumstancesFoundPh") : t("hc.circumstancesLostPh")} className="mt-1.5 rounded-xl border-findo-navy/20 focus-visible:ring-findo-red/40" data-testid={`${prefix}-circumstances`} />
      </div>
      <div><Label>{t("hc.color")} <Req /></Label>
        <ColorChips value={form.color} onChange={(v) => setForm((f) => ({ ...f, color: v }))} prefix={prefix} required />
      </div>
      <div><Label>{t("hc.condition")} <Req /></Label>
        <ConditionChips value={form.condition} onChange={(v) => setForm((f) => ({ ...f, condition: v }))} prefix={prefix} required />
      </div>
      <div><Label>{t("hc.brand")} <span className="text-xs font-normal text-slate-400">({t("hc.optional")})</span></Label>
        <BrandChips value={form.brand} onChange={(v) => setForm((f) => ({ ...f, brand: v }))} category={category ?? form.category} prefix={prefix} />
      </div>
    </div>
  );
};
